import { ReactNode, useId } from 'react';

interface ModalProps {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  style?: React.CSSProperties;
  /** Optional Tailwind text color class applied to title, close button and content container */
  textColorClass?: string;
  /** Optional classes applied to the close button (overrides part of default) */
  closeButtonClass?: string;
}

export default function Modal({
  title,
  onClose,
  children,
  className = '',
  contentClassName = '',
  headerClassName = '',
  style,
  textColorClass = 'text-gray-900',
  closeButtonClass,
}: ModalProps) {
  const titleId = useId();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-6 sm:items-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`relative max-h-[calc(100vh-3.5rem)] w-full max-w-[90vw] overflow-auto rounded-none bg-white/95 p-4 shadow-lg backdrop-blur sm:max-w-2xl sm:rounded-2xl sm:p-6 sm:shadow-2xl ${className}`}
        style={style}
        onClick={(event) => event.stopPropagation()}
      >
        {title && (
          <div
            className={`mb-4 flex items-start justify-between gap-4 ${headerClassName}`}
          >
            <h2
              id={titleId}
              className={`text-lg font-semibold ${textColorClass}`}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-full p-3 ${closeButtonClass || `text-gray-500 transition hover:bg-gray-100 hover:text-gray-700`} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              <span className="sr-only">Close</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M6 18L18 6"
                />
              </svg>
            </button>
          </div>
        )}

        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
}
